import { Injectable } from '@angular/core';
import { MessageKey } from '../../../models/message/message-key';
import { AddBulkReservationInformationResponse } from '../../../dtos/response/reservation-infomration-response/bulk-reservtion-infomration-response';
import { AddBulkReservationInformationResponseDetail } from '../../../dtos/response/reservation-infomration-response/add-bulk-reservation-information-response-detail';
import { ReservationInformationType } from '../../../dtos/response/reservation-infomration-response/reservation-information-type';

@Injectable({
  providedIn: 'root',
})
export class ContanctInformationFieldsMessageConverter {
  /**
   * Map message keys to the phone field response based on error conditions
   * @param AddBulkReservationInformationResponse The response from the phone field save
   * @returns An updated AddBulkReservationInformationResponse with mapped message keys
   */

  convertSaveContactInformationFieldsMessage(
    addBulkReservationInformationResponse: AddBulkReservationInformationResponse
  ): AddBulkReservationInformationResponse {
    if (!addBulkReservationInformationResponse) {
      return { mappedErrorKeys: [MessageKey.UNKNOWN_WITH_LINK] };
    }

    let mappedMessageKeys: MessageKey[] = [];

    // Timeout
    if (addBulkReservationInformationResponse.exceptionContent?.toLowerCase().includes('timeout')) {
      mappedMessageKeys.push(MessageKey.RESERVATION_INFO_SAVE_TIMEOUT);
    }
    // Password Decryption Error
    if (addBulkReservationInformationResponse.exceptionContent?.toLowerCase().includes('password decryption error')) {
      mappedMessageKeys.push(MessageKey.PASSWORD_DECRYPTION_ERROR);
    }

    // Handle billing address separately — Sabre may throw an error (city/postal mismatch)
    // but still save the address. Check updatedReservation to determine true vs. false failure.
    const billingAddressFailure = addBulkReservationInformationResponse.failedList?.find(
      (f) => f.reservationInformationType === ReservationInformationType.Address
    );
    if (billingAddressFailure) {
      const billingAddressActuallySaved = !!addBulkReservationInformationResponse.updatedReservation?.billingDetails?.billingAddresses?.[0];
      if (!billingAddressActuallySaved) {
        mappedMessageKeys.push(MessageKey.RESERVATION_INFO_BILLING_ADDRESS_SAVE_FAILURE);
      }
    }

    // Exclude billing address from remaining failure processing (phone/email have higher priority)
    const nonAddressFailedList =
      addBulkReservationInformationResponse.failedList?.filter(
        (f) => f.reservationInformationType !== ReservationInformationType.Address
      ) ?? [];

    // Partial failure
    if (nonAddressFailedList.length > 0 && addBulkReservationInformationResponse.successList?.length > 0) {
      mappedMessageKeys.push(MessageKey.RESERVATION_INFO_SAVE_PARTIAL_FAILURE);
    }
    // Mapped errors - specific to the error
    nonAddressFailedList.forEach((failure) => {
      mappedMessageKeys = this.convertSaveContactInformationFieldsErrorMessages(failure, mappedMessageKeys);
    });
    // If we've identified an error and made it this far without mapping a key, we'll map the unknown error key.
    if (
      addBulkReservationInformationResponse.statusCode !== 200 &&
      addBulkReservationInformationResponse.statusCode !== 207 &&
      mappedMessageKeys.length === 0
    ) {
      mappedMessageKeys.push(MessageKey.RESERVATION_INFO_SAVE_FAILURE_GENERIC);
    }

    return {
      ...addBulkReservationInformationResponse,
      mappedErrorKeys: mappedMessageKeys,
    };
  }

  convertSaveContactInformationFieldsErrorMessages(
    failure: AddBulkReservationInformationResponseDetail,
    mappedMessageKeys: MessageKey[]
  ): MessageKey[] {
    switch (failure.reservationInformationType) {
      case ReservationInformationType.CtceAdd:
      case ReservationInformationType.CtceRemove:
      case ReservationInformationType.CtcmAdd:
      case ReservationInformationType.CtcmRemove:
      case ReservationInformationType.CtcrAdd:
      case ReservationInformationType.CtcrRemove:
        mappedMessageKeys.push(MessageKey.RESERVATION_INFO_CTC_SSR_ADD_OR_REMOVE_FAILURE);
        break;
    }
    if (failure.failureMessage?.toLowerCase().includes('simultaneous changes')) {
      mappedMessageKeys.push(MessageKey.RESERVATION_INFO_SAVE_SIMULTANEOUS_CHANGES);
    }
    return mappedMessageKeys;
  }
}
